import { countsAsAvailable, type Category } from '../../components/agent/scheduleDay'
import { coverageStateFor, type CoverageState } from './dailyAssignmentBoard'
import { resolveShiftTarget, resolveTarget, weekdayNameFor, type StaffingTarget } from './staffingTargetStore'

/**
 * Coverage against staffing targets, one date at a time: the comparison the
 * daily assignment board, the dashboard, the planning board, and the approval
 * inbox all make (docs/product/coverage-and-insights.md). Pure, so each caller
 * hands it the day's people and gets numbers back.
 */
export type CoveragePerson = {
  id: string
  category: Category
  detail?: string
  countsAsAvailable?: boolean
}

export type CoverageContext = {
  date: string
  team: string
  shift: string
}

export type CoverageFinding = {
  // The assignment's name, or WHOLE_SHIFT for a target that names none.
  assignment: string
  actual: number
  required: number
  state: CoverageState
}

export const WHOLE_SHIFT = 'Whole shift'

// How many of the given people meet a target's own qualification, or all of
// them when it asks for none. Moved here from the daily assignment board so
// every caller counts the same way.
export function qualifiedActual(
  people: { id: string }[],
  qualification: string | undefined,
  qualifications: Map<string, Set<string>>,
): number {
  if (!qualification) return people.length
  return people.filter((person) => qualifications.get(person.id)?.has(qualification) ?? false).length
}

/**
 * Every assignment with a target that day, and the whole shift when a target
 * names no assignment, each with its actual and required headcount and its
 * state. An assignment with no target is not judged. A target that asks for a
 * qualification is left out unless the caller supplies who holds what.
 */
export function coverageFor(
  people: CoveragePerson[],
  targets: StaffingTarget[],
  context: CoverageContext,
  qualifications?: Map<string, Set<string>>,
): CoverageFinding[] {
  const weekday = weekdayNameFor(context.date)
  const findings: CoverageFinding[] = []
  const named = Array.from(
    new Set(targets.filter((target) => target.team === context.team && target.shift === context.shift && target.assignment).map((target) => target.assignment as string)),
  ).sort()

  for (const assignment of named) {
    const target = resolveTarget(targets, { ...context, weekday, assignment })
    if (!target || (target.qualification && !qualifications)) continue
    const group = people.filter((person) => person.category === 'work' && person.detail === assignment)
    const actual = qualifiedActual(group, target.qualification, qualifications ?? new Map())
    findings.push({ assignment, actual, required: target.minimumHeadcount, state: coverageStateFor(actual, target.minimumHeadcount) })
  }

  const shiftTarget = resolveShiftTarget(targets, { ...context, weekday })
  if (shiftTarget && !(shiftTarget.qualification && !qualifications)) {
    const available = people.filter((person) => countsAsAvailable(person))
    const actual = qualifiedActual(available, shiftTarget.qualification, qualifications ?? new Map())
    findings.push({ assignment: WHOLE_SHIFT, actual, required: shiftTarget.minimumHeadcount, state: coverageStateFor(actual, shiftTarget.minimumHeadcount) })
  }
  return findings
}

export type LeaveImpactDay = { date: string; findings: CoverageFinding[] }

// docs/product/coverage-and-insights.md's recommendations: coverage supports
// approval, review recommended, or a coverage conflict.
export type LeaveImpactLevel = 'supported' | 'review' | 'conflict'

export type LeaveImpact = { level: LeaveImpactLevel; days: LeaveImpactDay[] }

/**
 * What approving one employee's full-day leave does to coverage on each date:
 * the days, and per day the assignments (or the whole shift) it leaves
 * understaffed or at risk. Only a figure the leave itself lowers is reported,
 * so a day that was already short for someone else's reason and is unaffected
 * stays out. `peopleOn` is everyone on the team that date as they stand now,
 * with any approved leave already counted as leave.
 */
export function leaveImpact(args: {
  employeeId: string
  dates: string[]
  peopleOn: (date: string) => CoveragePerson[]
  targets: StaffingTarget[]
  team: string
  shift: string
  qualifications?: Map<string, Set<string>>
}): LeaveImpact {
  const days: LeaveImpactDay[] = []
  for (const date of args.dates) {
    const before = args.peopleOn(date)
    if (!before.some((person) => person.id === args.employeeId)) continue
    const after = before.map((person) =>
      person.id === args.employeeId ? { ...person, category: 'leave' as const, detail: undefined, countsAsAvailable: false } : person,
    )
    const context = { date, team: args.team, shift: args.shift }
    const was = coverageFor(before, args.targets, context, args.qualifications)
    const findings = coverageFor(after, args.targets, context, args.qualifications).filter((finding) => {
      const earlier = was.find((candidate) => candidate.assignment === finding.assignment)
      return finding.state === 'understaffed' || finding.state === 'at-risk' ? (earlier ? earlier.actual !== finding.actual : true) : false
    })
    if (findings.length > 0) days.push({ date, findings })
  }
  const short = days.some((day) => day.findings.some((finding) => finding.state === 'understaffed'))
  return { level: short ? 'conflict' : days.length > 0 ? 'review' : 'supported', days }
}
